import {
  ANDROID_HREF, HD_URL, BRANDS_PAGE_URL, EVENT_REFINEMENTS
} from '../constants';
import { removeSpecialCharacters } from '../utils';

// ---- Internal Helper Functions ---- //

/**
 * Transforms refinement labels containing comparison symbols, literal or
 * encoded, into (Greater than ...) or (Less than ...)
 * and it adds parentheses when the source label doesnt include them.
 * Value boundary is " - " or end of string.
 *
 * @param {string} label - The refinement label to transform.
 * @returns {string} The transformed label.
 */
export const decodeComparisonSymbols = (label) => {
  if (!label) return '';

  const phrases = {
    '>': 'Greater than',
    '<': 'Less than'
  };

  return label
    .replaceAll('&gt;', '>')
    .replaceAll('&lt;', '<')
    // Already with parenthesis
    .replace(/\(\s*([<>])\s*/g, (_match, symbol) => `(${phrases[symbol]} `)
    // No parenthesis, stopping before " - " or end of string
    .replace(/\s*([<>])\s*(.*?)\s*(?=\s-\s|$)/g, (_match, symbol, value) => ` (${phrases[symbol]} ${value})`)
    .replace(/\s+/g, ' ')
    .trim();
};

// Replaces all occurrences of a specific refinement value in the array
// with a new value while preserving the original order.
const replaceRefinement = (refinements, from, to) => {
  return refinements.map((refinement) => (refinement === from ? to : refinement));
};

// Normalizes a breadcrumb object by ensuring it has default properties and values.
const normalizeBreadcrumb = (crumb) => {
  return {
    id: null,
    name: null,
    refinementKey: null,
    dimensionName: 'Category',
    ...crumb,
    label: crumb?.label ? decodeComparisonSymbols(crumb.label) : null,
  };
};

// Applies price refinement logic and returns a formatted price part and updated refinements.
const applyPriceRefinement = (processedRefinements) => {
  const priceRefinement = processedRefinements.find((refinement) => refinement.includes('$'));
  if (!priceRefinement) return { pricePart: '', updatedRefinements: processedRefinements };

  const [lowerRaw, upperRaw] = priceRefinement
    .split('-')
    .map((part) => part.replace(/\$/g, '').replace(/,/g, '').trim());

  const lower = Number(lowerRaw);
  const upper = Number(upperRaw);

  if (Number.isNaN(lower) || Number.isNaN(upper)) {
    return { pricePart: '', updatedRefinements: processedRefinements };
  }

  const updatedRefinements = [...processedRefinements];
  const index = updatedRefinements.indexOf(priceRefinement);

  if (index !== -1) {
    updatedRefinements.splice(index, 1); // remove from original position
  }

  if (upper > 0 && lower < upper) {
    return { pricePart: `Under $${upper.toLocaleString()}`, updatedRefinements };
  }

  // Only add back if not an "Under" case
  updatedRefinements.push(priceRefinement);
  return { pricePart: '', updatedRefinements };
};

// Builds a breadcrumb label string by joining refinements and categories with ' - ',
// and appends the price part if provided, followed by a trailing dash.
const buildBreadcrumbLabel = (refinements = [], category = '', pricePart = '') => {
  const label = [...refinements, category].filter(Boolean).join(' - ');
  if (!label && !pricePart) return '';
  return `${label}${pricePart ? ' ' + pricePart : ''} -`;
};

// Returns a formatted string of categories and refinements.
// Handles price refinements, normalizes certain labels, and adjusts output based on the number of categories.
const getRefinedCategories = (categories, refinements) => {
  if (categories.length === 0) {
    return refinements.join(', ');
  }

  const processedRefinements = [...refinements];
  let { pricePart, updatedRefinements } = applyPriceRefinement(processedRefinements);

  const normalizedRefinements = updatedRefinements
    .map((refinement) => (refinement === '4 & Up' ? 'Highly Rated' : refinement))
    .filter(Boolean);

  if (categories.length === 1) {
    const base = normalizedRefinements.length
      ? normalizedRefinements.join(', ')
      : categories[0];
    return [base, pricePart].filter(Boolean).join(' ').trim();
  }

  const lastCategory = categories[categories.length - 1];
  const base = normalizedRefinements.length
    ? [...normalizedRefinements].reverse().join(', ')
    : '';
  return [base, lastCategory, pricePart].filter(Boolean).join(' ').trim();
};

// Returns the first category as the department, or an empty string if categories is empty.
const getDepartment = (categories = []) => categories[0] || '';

// Returns a string with a formatted category label for the title,
// applying special cases for 'Sofa Beds' and 'Console Tables' (with no refinements).
const getCategoryTitle = (categories, refinements) => {
  const CONSOLE_TABLE_CATEGORY = 'Console Tables';
  const APPENDED_CONSOLE_TABLE_CATEGORY = 'Console Tables & Sofa Tables';
  const SOFA_BEDS = 'Sofa Beds';
  const APPENDED_SOFA_BEDS = 'Sofa Beds & Sleeper Sofas';

  const isEmptyRefinements = refinements?.length === 0;

  const lastCategory = categories[categories.length - 1];
  if (!lastCategory) return '';

  if (lastCategory === SOFA_BEDS) {
    return APPENDED_SOFA_BEDS;
  }

  if (lastCategory === CONSOLE_TABLE_CATEGORY && isEmptyRefinements) {
    return APPENDED_CONSOLE_TABLE_CATEGORY;
  }

  return lastCategory;
};

// Builds the SEO title tag for browse pages based on categories, refinements, and search state.
const getBrowseTitleTag = ({ pathWithQueryParams, categories, refinements, searchVal }) => {
  const isBrowseInSearch = pathWithQueryParams?.includes('Ntt-');
  const validCategories = Array.isArray(categories) ? categories : [];
  const validRefinements = Array.isArray(refinements) ? refinements : [];
  if (validCategories.length === 0 && validRefinements.length === 0) return 'Browse The Home Depot';
  if (isBrowseInSearch) return `Search Results for ${searchVal} at The Home Depot`;

  const categoryTitle = getCategoryTitle(validCategories, validRefinements);
  const hasFurniture = validCategories.indexOf('Furniture') !== -1;

  const processedRefinements = [...validRefinements];

  const {
    pricePart,
    updatedRefinements: baseRefinements
  } = applyPriceRefinement(processedRefinements);

  const hasBestRated = baseRefinements.includes('Best Rated');
  const hasFourAndUp = baseRefinements.includes('4 & Up');
  const hasPickUpToday = baseRefinements.includes('Pick Up Today');

  let updatedRefinements = [...baseRefinements];

  if (!hasBestRated) {
    if (hasFourAndUp) {
      updatedRefinements = replaceRefinement(updatedRefinements, '4 & Up', 'Highly Rated');
    } else if (hasPickUpToday && !hasFurniture) {
      updatedRefinements = replaceRefinement(updatedRefinements, 'Pick Up Today', 'In Stock Near Me');
    }
  }

  const breadCrumbLabel = buildBreadcrumbLabel(
    updatedRefinements,
    categoryTitle,
    pricePart
  );

  return breadCrumbLabel ? `${breadCrumbLabel} The Home Depot` : 'The Home Depot';
};

// ---- Exported Functions ---- //

/**
 * Processes an array of breadcrumb objects and separates them into categories and refinements.
 *
 * @param {Array<Object>} breadcrumbs - The breadcrumb objects to process.
 * @returns {[Array<string>, Array<string>]} - An array containing two arrays: categories and refinements.
 */
export const processBreadcrumbs = (breadcrumbs) => {
  const categories = [];
  const refinements = [];
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return [categories, refinements];
  }
  breadcrumbs.forEach((crumb) => {
    const dimension = crumb?.dimensionName;
    const label = crumb?.label;
    if (dimension === 'Category') {
      categories.push(label);
      return;
    }
    if (dimension === 'Review Rating' && label === '5') {
      refinements.push('Best Rated');
      return;
    }
    if (label !== 'Home') {
      refinements.push(decodeComparisonSymbols(label));
    }
  });

  return [categories, refinements];
};

/**
 * Generates a description for browse and search pages based on page type, categories, refinements, and other parameters
 *
 * @param {string} pageType - The type of page ('b' for browse, other for search).
 * @param {Array<string>} categories - Array of category names.
 * @param {Array<string>} refinements - Array of refinement labels.
 * @param {string} searchVal - The search value or keyword.
 * @param {boolean} isContentful - Whether the description should be overridden for Contentful.
 * @param {boolean} isContentPageOnly - Whether to use the parsed template description for content pages only.
 * @param {string} parsedTemplateDescription - The description from the parsed template,
 * used if `isContentPageOnly` is true.
 * @returns {string} - The generated description string.
 */
export const getDescription = (
  pageType,
  categories = [],
  refinements = [],
  searchVal,
  isContentful,
  isContentPageOnly = false,
  parsedTemplateDescription
) => {

  const browseAppliances = categories[0] === 'Appliances' && pageType === 'b';
  if (isContentPageOnly && !browseAppliances) {
    return parsedTemplateDescription;
  }

  if (pageType !== 'b') {
    return `Search Results for ${searchVal} at The Home Depot`;
  }

  // Special case: 'Pick Up Today' (only when NOT Furniture and we have a category)
  const hasPickUpToday = refinements.includes('Pick Up Today');
  const hasFurniture = categories.includes('Furniture');
  if (hasPickUpToday && !hasFurniture && categories.length > 0) {
    const withoutPickUp = refinements.filter((refinement) => refinement !== 'Pick Up Today');
    const refinementsText = [...withoutPickUp].reverse().filter(Boolean).join(', ');
    const lastCategory = categories[categories.length - 1];

    // Build 'Find <refinements> <lastCategory> ready to be picked up today...'
    const prefix = refinementsText ? `Find ${refinementsText} ` : 'Find ';
    return `${prefix}${lastCategory} ready to be picked up today at your local Home Depot store.`;
  }

  const refinedCategories = getRefinedCategories(categories, refinements);
  const department = getDepartment(categories);

  // Special case: Major Appliances description (top-level Appliances)
  if (browseAppliances) {
    return `Browse our online aisle of ${refinedCategories}. `
      + `Shop The Home Depot for all your ${department} and DIY needs.`;
  }

  // Contentful override (only for the default path, matching original behavior)
  if (isContentful) {
    // eslint-disable-next-line max-len
    return `Shop ${refinedCategories} and more at The Home Depot. We offer free delivery, in-store and curbside pick-up for most items.`;
  }

  // Default description (with optional department suffix)
  // eslint-disable-next-line max-len
  const description = `Get free shipping on qualified ${refinedCategories} products or Buy Online Pick Up in Store today`
    + (department ? ` in the ${department} Department` : '');

  return `${description}.`;
};

/**
 * Generates the SEO title tag for browse and search pages.
 *
 * @param {string} pageType - The type of page ('b' for browse, 's' for search, others for default).
 * @param {Array<string>} [categories=[]] - Category names for the page, used to generate the title.
 * @param {Array<string>} [refinements=[]] - Array of refinement labels.
 * @param {string} searchVal - The search value or keyword.
 * @param {string} pathWithQueryParams - The URL path with query parameters.
 * @param {boolean} isContentPageOnly - Whether to use the parsed template title for content pages only.
 * @param {string} parsedTemplateTitle - The title from the parsed template, used if `isContentPageOnly` is true.
 * @returns {string} - The generated title tag string.
 */
export const getTitleTag = ({
  pageType,
  categories,
  refinements,
  searchVal,
  pathWithQueryParams,
  isContentPageOnly,
  parsedTemplateTitle
}) => {
  if (isContentPageOnly) {
    return parsedTemplateTitle;
  }

  switch (pageType) {
  case 'b':
    return getBrowseTitleTag({
      pathWithQueryParams,
      categories,
      refinements,
      searchVal
    });

  case 's':
    return `Search Results for ${searchVal} at The Home Depot`;
  default:
    return 'The Home Depot';
  }
};

/**
 * Normalizes the SEO navigation parameter by ensuring it starts with 'N-'.
 *
 * @param {string} seoNavParam - The SEO navigation parameter to normalize.
 * @returns {string|null} - The normalized navigation parameter starting with 'N-',
 * or `null` if the input is falsy.
 */
export const getNormalizedNavParam = (seoNavParam) => {
  if (!seoNavParam) return null;
  return seoNavParam.startsWith('N-') ? seoNavParam : `N-${seoNavParam}`;
};

/**
 * Extracts the value following the 'N-' prefix from a canonical URL.
 *
 * @param {string} canonical - The canonical URL to extract the value from.
 * @returns {string|null} - The extracted value if found, otherwise `null`.
 *
 */
export const extractNValue = (canonical) => {
  const match = canonical?.match(/\/N-([A-Za-z0-9]+)/);
  return match ? match[1] : null;
};

/**
 * Parses an array of breadcrumb objects, optionally prepending search-related crumbs if a keyword is provided.
 *
 * @param {Array<Object>} crumbs - The breadcrumb objects to process.
 * @param {string} [keyword] - Optional search keyword to add as a breadcrumb.
 * @param {boolean} [returnAll=false] - If true, returns all crumbs; otherwise, may filter to category crumbs.
 * @returns {Array<Object>} - The normalized array of breadcrumb objects.
 */
export const parseCrumbs = (crumbs = [], keyword, returnAll = false) => {
  let result = [...(Array.isArray(crumbs) ? crumbs : [])];
  if (keyword) {
    const textSearchCrumb = {
      url: '',
      label: 'Text Search'
    };

    let label;
    try {
      label = decodeURI(keyword);
    } catch (error) {
      label = keyword;
    }
    const keywordCrumb = {
      url: `/s/${keyword}?NCNI-5`,
      label
    };
    const hasTextSearchCrumb = result.some(
      (crumb) => crumb?.label === textSearchCrumb.label
    );

    if (!hasTextSearchCrumb) {
      result = [textSearchCrumb, keywordCrumb, ...result];
    }
  }

  const isCategory = (crumb) => (crumb?.dimensionName || '').toLowerCase() === 'category';

  const categoryCrumbs = result.filter(isCategory);
  const hasMultipleCategories = categoryCrumbs.length > 1;

  const breadcrumbs = hasMultipleCategories && !returnAll
    ? categoryCrumbs
    : result.filter((crumb) => !crumb?.deselectUrl);

  return breadcrumbs.map((crumb) => normalizeBreadcrumb(crumb));
};

/**
 * Determines whether a page's breadcrumbs represent a Stand-Alone Brand Page: a page whose
 * only breadcrumb is a single `Brand` dimension crumb.
 *
 * @param {Array<Object>} crumbs - The page's raw (unparsed) breadcrumbs.
 * @returns {boolean} True if the page is a Stand-Alone Brand Page.
 */
export const isStandAloneBrandPage = (crumbs = []) => (
  crumbs?.length === 1 && crumbs?.[0]?.dimensionName?.toLowerCase() === 'brand'
);

/**
 * Builds the breadcrumb list for a stand-alone Brand Page's structured data, replacing the
 * single raw brand crumb with Home -> Brands -> <Brand Name>. The Android alternate-link tag
 * is unaffected and keeps using the real brand crumb.
 *
 * @param {Array<Object>} processedBreadcrumbs - The page's resolved breadcrumbs. For a
 *   Stand-Alone Brand Page this is the raw brand crumb (not the parsed/filtered trail),
 *   since parseCrumbs drops it due to its deselectUrl.
 * @param {string} canonical - The resolved canonical URL for the page.
 * @param {boolean} [skipBrandsHub=false] - Omit the generic Brands hub crumb, used when an
 *   event overlay makes the trail Home / Event / Event Brand.
 * @returns {Array<Object>} Breadcrumbs to use for schema.org structured data.
 */
export const getStandAloneBrandPageCrumbs = (processedBreadcrumbs, canonical, skipBrandsHub = false) => ([
  ...(skipBrandsHub ? [] : [{ label: 'Brands', id: `${HD_URL}${BRANDS_PAGE_URL}` }]),
  { label: processedBreadcrumbs?.[0]?.label, id: canonical }
]);

// ---- Event overlay (/b/ event pages only) ---- //

/**
 * Finds the active event refinement (see EVENT_REFINEMENTS) among the raw crumbs of a browse page.
 *
 * @param {Array<Object>} crumbs - The page's raw breadcrumbs.
 * @param {string} pathWithQueryParams - The page path; only `/b/` pages will have an event overlay.
 * @returns {{ event: Object, eventCrumb: Object } | null} The matched event and its crumb, or null.
 */
export const getEventMatch = (crumbs = [], pathWithQueryParams = '') => {
  if (!pathWithQueryParams?.includes('/b/')) return null;
  const matchedCrumb = crumbs?.find((crumb) => EVENT_REFINEMENTS[crumb?.refinementKey]);
  if (!matchedCrumb) return null;

  const event = EVENT_REFINEMENTS[matchedCrumb.refinementKey];
  return { event, eventCrumb: { label: event.label, url: event.url } };
};

// Inserts the event slug as a path segment immediately before the N-Value
// segment, and tags the nav param with the event's refinementKey.
// e.g. /b/Tools/N-5yc1vZc1xy becomes /b/Tools/Black-Friday-Deals/N-5yc1vZc1xyZ1z1zdoh
// Returns null when the url has no N-Value segment to rewrite.
const insertEventSlug = (url, slug, refinementKey) => {
  if (!url) return url;
  const [path, query] = url.split('?');
  const queryString = query ? `?${query}` : '';

  const segments = path.split('/');
  const nValueIndex = segments.findIndex((segment) => segment.startsWith('N-'));
  if (nValueIndex === -1) return null;

  const refinements = segments[nValueIndex].split('Z').slice(1);
  if (!refinements.includes(refinementKey)) {
    segments[nValueIndex] += `Z${refinementKey}`;
  }

  if (segments[nValueIndex - 1] !== slug) {
    segments.splice(nValueIndex, 0, slug);
  }

  return `${segments.join('/')}${queryString}`;
};

// Leaves crumbs with no label or link (e.g. Home) untouched rather than half-rewriting them.
const applyEventToCrumb = (crumb, event) => {
  const label = crumb?.label || crumb?.name;
  const hasLink = crumb?.id || crumb?.url;
  if (!label || !hasLink) return crumb;

  const id = crumb.id && insertEventSlug(crumb.id, event.slug, event.refinementKey);
  const url = crumb.url && insertEventSlug(crumb.url, event.slug, event.refinementKey);
  // A link we couldn't rewrite would leave an event-prefixed label pointing at a non-event page.
  if ((crumb.id && !id) || (crumb.url && !url)) return crumb;

  const prefixedLabel = `${event.label} ${label}`;
  return {
    ...crumb,
    ...(id && { id }),
    ...(url && { url }),
    ...(crumb.label && { label: prefixedLabel }),
    ...(crumb.name && { name: prefixedLabel })
  };
};

/**
 * Removes the matched event crumb from the raw crumbs so it isn't parsed, or counted by
 * isStandAloneBrandPage, as a regular crumb.
 *
 * @param {Array<Object>} crumbs - The page's raw breadcrumbs.
 * @param {Object|null} eventMatch - The result of getEventMatch.
 * @returns {Array<Object>} The crumbs without the event refinement.
 */
export const excludeEventCrumb = (crumbs = [], eventMatch = null) => (
  eventMatch
    ? crumbs.filter((crumb) => crumb?.refinementKey !== eventMatch.event.refinementKey)
    : crumbs
);

// Puts the event crumb first, then rewrites the rest of the trail to reflect the active event,
// e.g. Home / Tools becomes Home / Black Friday / Black Friday Tools.
const overlayEventCrumbs = (crumbs, { event, eventCrumb }) => [
  eventCrumb,
  ...crumbs.map((crumb) => applyEventToCrumb(crumb, event))
];

/**
 * Resolves the crumbs used for a browse/search page's structured data, including the event
 * overlay. The Android alternate tag is unaffected and is built by the callers from the raw crumbs.
 *
 * @param {Object} params
 * @param {Array<Object>} params.crumbs - The page's raw breadcrumbs.
 * @param {string} [params.keyword] - The search keyword, if any.
 * @param {string} params.canonical - The resolved canonical URL for the page.
 * @param {string} params.pathWithQueryParams - The page path.
 * @returns {Array<Object>} Breadcrumbs to use for schema.org structured data.
 */
export const resolveSchemaCrumbs = ({
  crumbs, keyword, canonical, pathWithQueryParams
}) => {
  const eventMatch = getEventMatch(crumbs, pathWithQueryParams);
  const baseCrumbs = excludeEventCrumb(crumbs, eventMatch);

  const baseSchemaCrumbs = isStandAloneBrandPage(baseCrumbs)
    ? getStandAloneBrandPageCrumbs(baseCrumbs, canonical, Boolean(eventMatch))
    : parseCrumbs(baseCrumbs, keyword);

  return eventMatch ? overlayEventCrumbs(baseSchemaCrumbs, eventMatch) : baseSchemaCrumbs;
};

/**
 * Determines if a page should have a `NOINDEX/NOFOLLOW` meta tag based on query parameters and canonical URL.
 *
 * @param {string} pathWithQueryParams - The URL path with query parameters.
 * @param {string} canonicalUrl - The canonical URL of the page.
 * @returns {boolean} - Returns `true` if `NOINDEX/NOFOLLOW` should be applied, otherwise `false`.
 */
export const getAllowedNoFollow = (pathWithQueryParams, canonicalUrl) => {
  const allLowercaseQuery = pathWithQueryParams?.toLowerCase();
  const allowedUpperbound = /upperbound=(?:100|(?:2[05]|[3-6]0))0[&/]?$/;
  const upperbound = allLowercaseQuery?.split('&')?.filter(((element) => element.match(allowedUpperbound)));

  const queryParams = [
    'NCNI-5',
    'Ns=P_Topseller_Sort%7C1',
    'Ns=P_Smartsort%7C1',
    'Ns=P_REP_PRC_MODE%7C0',
    'Ns=P_REP_PRC_MODE%7C1',
    'Ns=P_Top_Rated_Sort%7C1',
    'sortorder=',
    'sortby=',
    'Ntk-',
    '/b/Featured-Products',
    'Ntt-'
  ];
  if (
    allLowercaseQuery?.includes('/b/furniture-living-room-furniture-sofas-couches')
    && (allLowercaseQuery?.includes('lowerbound=0') && upperbound?.length)) {
    return false;
  }

  return canonicalUrl?.includes('NCNI-5')
    || queryParams.some((query) => pathWithQueryParams?.includes(query))
    || allLowercaseQuery?.includes('lowerbound=')
    || allLowercaseQuery?.includes('upperbound=');
};

/**
 * Builds an Android alternate link tag from the first breadcrumb.
 *
 * @param {Array<{ label: string }>} processedBreadcrumbs - Breadcrumbs array.
 * @param {string|number} nValue - The N-Value used in the URL.
 * @returns {{ href: string, media: string } | null} The Android alternate tag or null.
 */
export const buildAndroidAlternateTag = (processedBreadcrumbs = [], nValue) => {

  if (!Array.isArray(processedBreadcrumbs) || processedBreadcrumbs.length === 0) {
    return null;
  }

  const firstLabel = processedBreadcrumbs[0].label;
  if (!firstLabel) return null;

  const formattedLabel = removeSpecialCharacters(firstLabel).replace(/\s+/g, '-');

  const href = `${ANDROID_HREF}categoryid/${nValue}/${formattedLabel}`;

  return {
    href,
    media: 'only screen and (max-width: 640px)'
  };
};
